import React, { useState, useCallback, useMemo } from 'react';
import ModalDefault from '../ModalDefault';
import DateTimePicker from '../Demanda/DateTimePicker';
import ButtonDefault from '../form/ButtonDefault';
import { IconClose } from '../icons';
import toast from '../../services/toast';
import {
  ModalContent,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalBody,
  InstructionText,
  FieldGroup,
  Label,
  DateTimeContainer,
  DatePickerContainer,
  TimezoneReadonly,
  ErrorMessage,
  ModalFooter,
  ButtonGroup
} from './styles';

export type TimezoneCode = 'FNT' | 'BRT' | 'AMT' | 'ACT';

interface ModalDefinirPrazoAceiteProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (prazoAceite: string) => void;
  prazoFatal?: Date;
  prazoConclusao?: Date;
  timezone?: string;
  prazoAceiteInicial?: string;
}

const timezoneShort = (tz: string | undefined) => {
  switch (tz) {
    case 'FNT': return 'FNT';
    case 'BRT': return 'BRT';
    case 'AMT': return 'AMT';
    case 'ACT': return 'ACT';
    default: return tz || 'BRT';
  }
};

const ModalDefinirPrazoAceite: React.FC<ModalDefinirPrazoAceiteProps> = ({
  open,
  onClose,
  onConfirm,
  prazoFatal,
  prazoConclusao,
  timezone,
  prazoAceiteInicial
}) => {
  const [dataAceite, setDataAceite] = useState<string>('');
  const [horaAceite, setHoraAceite] = useState<string>('');
  const [erro, setErro] = useState<string>('');

  React.useEffect(() => {
    if (prazoAceiteInicial) {
      const date = new Date(prazoAceiteInicial);
      if (!isNaN(date.getTime())) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        setDataAceite(`${year}-${month}-${day}`);
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        setHoraAceite(`${hours}:${minutes}`);
      }
    } else {
      setDataAceite('');
      setHoraAceite('');
    }
    setErro('');
  }, [prazoAceiteInicial, open]);

  const onChangeAceite = useCallback((dataStr: string, timeStr: string) => {
    setDataAceite(dataStr);
    setHoraAceite(timeStr);
    setErro('');
  }, []);

  const valorAceiteCompleto = useMemo(() => {
    if (!dataAceite) return '';
    if (!horaAceite || horaAceite === '--:--') return dataAceite;
    return `${dataAceite} ${horaAceite}`;
  }, [dataAceite, horaAceite]);

  const validar = useCallback(() => {
    if (!dataAceite || !horaAceite || horaAceite === '--:--') {
      setErro('Prazo de aceite é obrigatório');
      return false;
    }

    const [y, m, d] = dataAceite.split('-').map(Number);
    const [hh, mm] = horaAceite.split(':').map(Number);
    const dtAceite = new Date(y, m - 1, d, hh, mm);
    const agora = new Date();

    if (dtAceite < agora) {
      setErro('Prazo de aceite não pode ser uma data/hora passada');
      return false;
    }

    if (prazoFatal && dtAceite >= prazoFatal) {
      toast.error('Prazo de aceite deve ser anterior ao Prazo Fatal');
      setErro('Prazo de aceite deve ser anterior ao Prazo Fatal');
      return false;
    }

    if (prazoConclusao && dtAceite >= prazoConclusao) {
      toast.error('Prazo de aceite deve ser anterior ao Prazo de Conclusão');
      setErro('Prazo de aceite deve ser anterior ao Prazo de Conclusão');
      return false;
    }

    setErro('');
    return true;
  }, [dataAceite, horaAceite, prazoFatal, prazoConclusao]);

  const handleConfirm = useCallback(() => {
    if (!validar()) {
      return;
    }

    const [y, m, d] = dataAceite.split('-').map(Number);
    const [hh, mm] = horaAceite.split(':').map(Number);
    const prazoAceiteISO = `${dataAceite}T${horaAceite}:00`;
    
    onConfirm(prazoAceiteISO);
  }, [dataAceite, horaAceite, validar, onConfirm]);

  return (
    <ModalDefault
      id="modal-definir-prazo-aceite"
      openModal={open}
      onClose={onClose}
      width="500px"
    >
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Definir Prazo de Aceite</ModalTitle>
          <CloseButton onClick={onClose}>
            <IconClose color="#6b7280" />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <InstructionText>
            Defina um prazo de aceite coerente com o Prazo Fatal e o de Conclusão
          </InstructionText>

          <FieldGroup>
            <Label required>Prazo para aceite da Demanda:</Label>
            <DateTimeContainer>
              <DatePickerContainer>
                <DateTimePicker
                  value={valorAceiteCompleto}
                  onChange={onChangeAceite}
                  placeholder="dd/mm/aaaa | hh:mm"
                  error={!!erro}
                />
              </DatePickerContainer>
              <TimezoneReadonly>{timezoneShort(timezone)}</TimezoneReadonly>
            </DateTimeContainer>
            {erro && <ErrorMessage>{erro}</ErrorMessage>}
          </FieldGroup>
        </ModalBody>

        <ModalFooter>
          <ButtonGroup>
            <ButtonDefault
              className="cancel"
              onClick={onClose}
            >
              Cancelar
            </ButtonDefault>
            <ButtonDefault
              className="continue"
              onClick={handleConfirm}
            >
              Confirmar
            </ButtonDefault>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </ModalDefault>
  );
};

export default ModalDefinirPrazoAceite;

